import { ArrowDownward, ArrowUpward, Notifications } from '@mui/icons-material';
import { Alert, AlertTitle, Badge, Button, Collapse, IconButton } from '@mui/material';
import { Stack } from '@mui/system';
import { NoticeContent, documentSlice } from 'cdx-common';
import { DateTime } from 'luxon';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, appStore } from '../store';

export interface ToastContent {
    visible: boolean;
    action?: React.ReactNode;
    type?: 'error' | 'info' | 'success' | 'warning';
    text: string;
    url?: string;
    created: number;
}

export interface ToasterInterface {
    toast: {
        show: (Content: Omit<ToastContent, 'visible' | 'created'>) => void,
        success: (text: string) => void,
        info: (text: string) => void,
        error: (text: string) => void,
        warning: (text: string) => void,
    };
}
const ToasterContext = createContext({} as unknown as ToasterInterface);

const TOAST_DURATION: number = 5000; // 8 secondes

const Toast = (props: { item: NoticeContent, explicitlyOpened?: boolean; }) => {


    const [toastTimeout, setToastTimeout] = useState<number | undefined>();
    const [opened, setOpenend] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const delay: number = DateTime.now().valueOf() - props.item.created + TOAST_DURATION;
        if (delay > 0) {
            setOpenend(true);
            setToastTimeout(setTimeout(() => {
                setOpenend(false);
            }, delay));
        }
    }, [props.item.created]);

    useEffect(() => {
        setOpen(props.explicitlyOpened === true || (props.explicitlyOpened === undefined && opened));
    }, [props.explicitlyOpened, opened]);

    useEffect(() => {
        return () => {
            if (toastTimeout) {
                clearTimeout(toastTimeout);
            }
        };
    }, []);
    return <>{<Collapse in={open}><Alert elevation={2} severity={props.item.type}>{props.item.title && <AlertTitle>{props.item.title}</AlertTitle>}{props.item.text}</Alert></Collapse>}</>;
};

let toastIdInc: number = 1;

export const Toaster = (props: { children: React.ReactNode; }) => {
    const notices = useSelector((state: RootState) => state.document.notices);

    const addToast = (content: Omit<ToastContent, 'visible' | 'created'>) => {
        const id: number = ++toastIdInc;
        appStore.dispatch(documentSlice.actions.notice({
            ...content,
            id,
            visible: true,
            created: DateTime.now().valueOf()
        }));
        setTimeout(() => {
            appStore.dispatch(documentSlice.actions.hideNotice(id));
        }, TOAST_DURATION);
    };

    const show = (content: Omit<ToastContent, 'visible' | 'created'>) => {
        addToast(content);
    };
    const success = (text: string) => { addToast({ text, type: 'success' }); };
    const info = (text: string) => { addToast({ text, type: 'info' }); };
    const error = (text: string) => { addToast({ text, type: 'error' }); };
    const warning = (text: string) => { addToast({ text, type: 'warning' }); };

    return <ToasterContext.Provider value={{
        toast: {
            show,
            success,
            info,
            error,
            warning
        }
    }}>{props.children}<Stack sx={{ position: "absolute", bottom: 10, width: '100%' }} spacing={2}>
            {notices.filter(p => p.visible).map((p, index) => <Alert key={index} sx={{ alignSelf: 'center' }} severity={p.type}>{p.text}</Alert>)}
        </Stack>
    </ToasterContext.Provider>;
};

export const useToaster = () => useContext(ToasterContext);


export const CustomToaster = () => {

    const notices = useSelector((state: RootState) => state.document.notices);
    const [openendToasts, setOpenendToasts] = useState<NoticeContent[] | undefined>();

    const toggleNotices = () => {
        if (openendToasts) {
            setOpenendToasts(undefined);
        } else {
            setOpenendToasts(notices.reduceRight((prev: NoticeContent[], curr) => {
                if (prev.length < 3) {
                    prev.unshift(curr);
                }
                return prev;
            }, []));
        }
    };

    const showOldest = () => {
        if (openendToasts && openendToasts.length) {
            // is there a older notice ?
            const copy = [...openendToasts];
            let i: number = notices.findIndex(p => p === openendToasts[0]);
            if (i > 0) {
                copy.pop();
                copy.unshift(notices[i - 1]);
                setOpenendToasts([...copy]);
            }
        }
    };
    const showNewest = () => {
        if (openendToasts && openendToasts.length) {
            const copy = [...openendToasts];
            let i: number = notices.findIndex(p => p === openendToasts[openendToasts.length - 1]);
            if (i < (notices.length - 1)) {
                copy.shift();
                copy.push(notices[i + 1]);
                setOpenendToasts([...copy]);
            }
        }
    };

    return <>
        <Stack sx={{ position: "absolute", bottom: 24, left: 24 }} spacing={2}>
            {notices.map(p => <Toast item={p} explicitlyOpened={openendToasts?.includes(p)} />)}
            {notices.length ? <div>
                <IconButton aria-label='Notifications' onClick={toggleNotices}>
                    <Badge sx={{ padding: '0 4px' }} badgeContent={notices.length} color='info'>
                        <Notifications />
                    </Badge>
                </IconButton>
                {openendToasts ? <>
                    <Button size='small'><ArrowUpward onClick={showOldest} /></Button>
                    <Button size='small'><ArrowDownward onClick={showNewest} /></Button>
                </> : null}
            </div> : null}
        </Stack></>;
};
